import React, { useEffect, useMemo, useState } from 'react'
import { Box, Grid2 as Grid, useTheme } from '@mui/material'
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom'
import NavLinkBack from 'components/NavLinkBack'
import PatientInfo from 'components/PatientInfo'
import { StyledTab, StyledTabs } from 'components/TabNavigator'
import {
  AssignmentOutlined,
  EventNote,
  HealthAndSafetyOutlined,
  SickOutlined
} from '@mui/icons-material'
import {
  MEDICAL_EVENTS,
  MEDICAL_INFO,
  PATIENT_HEALTH_DATA,
  PATIENT_LIST,
  SYMPTOM_TRACKING
} from 'navigation/CONSTANTS'
import { usePreventNavigation } from 'navigation/preventNavigation'
import ButtonBase from 'design-system/button'
import ConfirmationModal from 'components/ConfirmationModal'
import { deleteEstablishmentPatientRelation } from 'services/establishment'
import { useAppSelector } from 'reduxStore/store'
import { useToast } from 'services/context/toast'
import { useShowBannerHDA } from 'utils/hda'
import { useClinicalTrial } from 'services/context/clinicalTrial'
import ClinicalTrialModal from './components/ClinicalTrialModal'

const HealthDataPage: React.FC = () => {
  // params
  const { patientId } = useParams<{ patientId: string }>()
  // state
  const [activeTab, setActiveTab] = useState<number>(0)
  const { message, setMessageToPrevent } = usePreventNavigation()
  const [openModal, setOpenModal] = useState<boolean>(false)
  const [openClinicalTrialModal, setOpenClinicalTrialModal] =
    useState<boolean>(false)
  const [deletionState, setDeletionState] = useState<IStateStdTypes>('idle')
  const establishmentId = useAppSelector(
    state => state.establishment.data?.establishment_id
  )
  const toast = useToast()
  // navigation
  const navigate = useNavigate()
  const location = useLocation()
  const theme = useTheme()

  const {
    clinicalTrial,
    state,
    addNewClinicalTrial,
    updateStatus,
    updateStatusTo,
    deleteClinicalTrial
  } = useClinicalTrial()

  const isHDA = useShowBannerHDA()

  const backgroundColor = useMemo(() => {
    if (activeTab === 0 && theme.palette.mode === 'dark') {
      return theme.palette.tabBorder.main
    }
    return undefined
  }, [theme, activeTab])

  const tabs: Array<{
    label: string
    path: string
    icon?:
      | string
      | React.ReactElement<unknown, string | React.JSXElementConstructor<any>>
  }> = [
    { label: 'Saúde', path: '', icon: <HealthAndSafetyOutlined /> },
    {
      label: 'Ficha Médica',
      path: `/${MEDICAL_INFO}`,
      icon: <AssignmentOutlined />
    },
    {
      label: 'Eventos Médicos',
      path: `/${MEDICAL_EVENTS}`,
      icon: <EventNote />
    },
    {
      label: 'Dor e sintomas',
      path: `/${SYMPTOM_TRACKING}`,
      icon: <SickOutlined />
    }
  ]

  useEffect(() => {
    const current = (() => {
      const path =
        location.pathname.split(PATIENT_HEALTH_DATA(patientId))[1] || ''

      const idx = path.indexOf('/', 1)
      const result = idx === -1 ? path : path.substring(0, idx)
      return result
    })()

    const index = tabs.findIndex(item => item.path === current)

    setActiveTab(index)
  }, [location])

  const onChange = () => {
    if (message) {
      const confirmed = window.confirm(message)
      if (confirmed) {
        setMessageToPrevent()
      }
      return confirmed
    }
    return true
  }

  const navigateTO = (path: string = '') => {
    navigate(`/${PATIENT_HEALTH_DATA(patientId)}${path}`)
  }

  const handleChange = (_: any, newValue: number) => {
    const ableToChange = onChange ? onChange() : true
    if (ableToChange) {
      setActiveTab(newValue)
      navigateTO(tabs[newValue].path)
    }
  }

  const handleConfirmDeletion = async () => {
    if (deletionState !== 'loading') {
      setDeletionState('loading')
      const response = await deleteEstablishmentPatientRelation(
        patientId ?? '',
        establishmentId ?? ''
      )

      if (response.success) {
        setDeletionState('success')
        toast.handler({
          content: 'Paciente removido com sucesso!',
          severity: 'success'
        })
        navigate(`/${PATIENT_LIST}`, { replace: true })
      } else {
        setDeletionState('error')
        toast.handler({
          content: 'Houve um erro ao tentar remover o paciente',
          severity: 'error'
        })
      }

      setOpenModal(false)
    }
  }

  const handleWatchNotReturned = async () => {
    // In 'active': move to 'pending_watch_return'
    if (clinicalTrial?.status === 'active') {
      await updateStatusTo(
        'pending_watch_return',
        'Relógio marcado como pendente'
      )
    }
    // In 'pending_watch_return': just stay in same state
    else if (clinicalTrial?.status === 'pending_watch_return') {
      toast.handler({
        content: 'OK, aguardando devolução',
        severity: 'info'
      })
    }
  }

  const handleReactivateStudy = async () => {
    await updateStatusTo('active', 'Estudo reativado com sucesso')
  }

  const handleFinalizeClinicalTrial = async (
    status: 'success' | 'discarded'
  ) => {
    await deleteClinicalTrial(
      status,
      `Estudo ${status === 'success' ? 'concluído' : 'descartado'}!`
    )
  }

  return (
    <Box sx={{ padding: 2, width: '100%', maxWidth: 1200, margin: '0 auto' }}>
      <Grid container>
        <Grid size={12} paddingBlock={2}>
          <NavLinkBack to={`/${PATIENT_LIST}`} title='Lista de Pacientes' />
        </Grid>
      </Grid>
      <PatientInfo patientId={patientId ?? ''} />
      <Grid container size={12} justifyContent='end'>
        <StyledTabs
          value={activeTab}
          onChange={handleChange}
          variant='scrollable'
          scrollButtons='auto'
          aria-label='tab navigator'
          slotProps={{
            indicator: {
              sx: {
                background: 'secondary.main'
              }
            }
          }}
        >
          {tabs.map((tab, index) => (
            <StyledTab
              icon={tab.icon}
              label={tab.label}
              className={activeTab === index ? 'selected' : ''}
              key={`${tab.label}-${index}`}
              sx={{ fontSize: '18px', paddingInline: '32px' }}
            />
          ))}
        </StyledTabs>
      </Grid>
      <Grid
        container
        sx={{
          borderRadius: '8px 0 8px 8px',
          boxShadow: `0 2px 8px ${theme.palette.action.focus}`,
          marginBottom: theme.spacing(4),
        }}
      >
        <Outlet />
      </Grid>
      <Grid container justifyContent={'end'} spacing={2}>
        {isHDA &&
          (clinicalTrial?.status === 'active' ||
            clinicalTrial?.status === 'pending_watch_return') && (
            <ButtonBase
              variant='outlined'
              color={
                clinicalTrial?.status === 'pending_watch_return'
                  ? 'error'
                  : 'warning'
              }
              sx={{ marginTop: '16px' }}
              loading={state == 'loading'}
              disabled={state == 'loading'}
              onClick={() => setOpenClinicalTrialModal(true)}
            >
              {clinicalTrial?.status === 'pending_watch_return'
                ? 'O paciente devolveu o relógio?'
                : 'Concluir Estudo'}
            </ButtonBase>
          )}
        {isHDA &&
          (!clinicalTrial ||
            clinicalTrial.status == 'success' ||
            clinicalTrial.status == 'discarded') && (
            <ButtonBase
              variant='outlined'
              type='submit'
              sx={{
                marginTop: '16px'
              }}
              loading={state == 'loading'}
              disabled={state == 'loading'}
              onClick={
                clinicalTrial
                  ? () => updateStatus('Paciente adicionado no estudo')
                  : addNewClinicalTrial
              }
            >
              Adicionar paciente ao estudo
            </ButtonBase>
          )}
        <ButtonBase
          variant='contained'
          color='error'
          type='submit'
          sx={{
            marginTop: '16px'
          }}
          loading={deletionState == 'loading'}
          disabled={deletionState == 'loading'}
          onClick={() => setOpenModal(true)}
        >
          Remover paciente
        </ButtonBase>
      </Grid>
      <ConfirmationModal
        onCancel={() => setOpenModal(false)}
        onConfirm={handleConfirmDeletion}
        open={openModal}
        title='Deseja remover o paciente da organização?'
      />
      <ClinicalTrialModal
        open={openClinicalTrialModal}
        onClose={() => setOpenClinicalTrialModal(false)}
        currentStatus={
          clinicalTrial?.status as 'active' | 'pending_watch_return'
        }
        onWatchNotReturned={handleWatchNotReturned}
        onReactivateStudy={handleReactivateStudy}
        onProceedToFinalize={handleFinalizeClinicalTrial}
        loading={state === 'loading'}
      />
    </Box>
  )
}

export default HealthDataPage
